import { useEffect, useMemo, useState } from 'react';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  Header,
  Container,
  Table,
  useTable,
  CellText,
  Cell,
  Flex,
  Button,
  ButtonLink,
  Skeleton,
  Text,
  type ColumnConfig,
  type SortDescriptor,
  Box,
} from '@backstage/ui';
import { RiAddLine, RiDownloadLine } from '@remixicon/react';
import {
  useApi,
  identityApiRef,
  useRouteRef,
} from '@backstage/frontend-plugin-api';
import type {
  CheckDefinition,
  CheckImportResult,
} from '@internal/backstage-plugin-pulse-common';
import {
  usePulseChecks,
  useTrackChecks,
  useUrlMultiSelect,
  useUrlValue,
  useResetUrlParams,
  useCreateCheck,
  useMyTeams,
  type TrackCheckAggregate,
} from '../../hooks';
import { DefinitionListFilterBar } from '../definition-list-filter-bar';
import {
  getOwnerLabel,
  getOwnerOptions,
  getAffectedKindOptions,
  matchesAffectedKinds,
} from '../definition-facets';
import { checksDetailsSubRouteRef, checksNewSubRouteRef } from '../../routes';
import { paginationOptions, sortCollator } from '../config';
import { CheckActionsButton } from './check-actions';
import { CompliancePercentageBar } from '../tracks/compliance-indicators';
import { PulseListSkeleton } from '../list-skeleton';
import { CheckImportDialog } from './check-import-dialog';
import { CheckImportResultBanner } from './check-import-result-banner';
import { downloadChecksYaml } from './check-yaml';
import { checkImportResultSchema } from '../../schemas/checks';
import {
  getImportResultStorageKey,
  readImportResult,
  storeImportResult,
} from '../import-result-storage';

type CheckRow = CheckDefinition & {
  aggregate?: TrackCheckAggregate;
};

const CHECK_IMPORT_RESULT_STORAGE_KEY = 'pulse.checks.import-result';

const getSortableValue = (
  item: CheckRow,
  column: string,
  labelByRef: Map<string, string>,
): string | number => {
  switch (column) {
    case 'name':
      return item.name;
    case 'description':
      return item.description ?? '';
    case 'checks':
      return item.aggregate?.passedEntities ?? -1;
    case 'compliance':
      return item.aggregate?.percentage ?? -1;
    case 'ownerEntityRef':
      return getOwnerLabel(item.ownerEntityRef, labelByRef);
    default:
      return '';
  }
};

// The owner column renders catalog display names, so sorting has to use them too.
const createSortChecks =
  (labelByRef: Map<string, string>) =>
  (data: CheckRow[], sort: SortDescriptor): CheckRow[] => {
    const direction = sort.direction === 'descending' ? -1 : 1;
    const sortColumn = String(sort.column);

    return [...data].sort((left, right) => {
      const leftValue = getSortableValue(left, sortColumn, labelByRef);
      const rightValue = getSortableValue(right, sortColumn, labelByRef);
      const comparison =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : sortCollator.compare(
              String(leftValue).trim(),
              String(rightValue).trim(),
            );

      if (comparison !== 0) {
        return comparison * direction;
      }

      return sortCollator.compare(left.id, right.id);
    });
  };

const createColumns = (
  getDetailsPath: ((params: { id: string }) => string) | undefined,
  onDeleted: () => void,
  aggregatesLoading: boolean,
): ColumnConfig<CheckRow>[] => [
  {
    id: 'name',
    label: 'Name',
    isRowHeader: true,
    defaultWidth: '20fr',
    isSortable: true,
    cell: item => (
      <CellText
        title={item.name}
        href={getDetailsPath ? getDetailsPath({ id: item.id }) : undefined}
      />
    ),
  },
  {
    id: 'description',
    label: 'Description',
    defaultWidth: '25fr',
    isSortable: true,
    cell: item => <CellText title={item.description ?? ''} color="secondary" />,
  },
  {
    id: 'checks',
    label: 'Checks',
    defaultWidth: '15fr',
    isSortable: true,
    cell: item => {
      if (!item.aggregate && aggregatesLoading) {
        return (
          <Cell>
            <Skeleton width="80%" height="var(--bui-space-5)" />
          </Cell>
        );
      }
      return (
        <CellText
          title={
            item.aggregate
              ? `${item.aggregate.passedEntities} of ${item.aggregate.totalEntities} entities passing`
              : 'Unavailable'
          }
          color="secondary"
        />
      );
    },
  },
  {
    id: 'compliance',
    label: 'Compliance',
    defaultWidth: '10fr',
    isSortable: true,
    cell: item => {
      if (!item.aggregate && aggregatesLoading) {
        return (
          <Cell>
            <Skeleton width="100%" height="var(--bui-space-5)" />
          </Cell>
        );
      }

      if (!item.aggregate) {
        return <CellText title="Unavailable" color="secondary" />;
      }

      return (
        <Cell>
          <CompliancePercentageBar percentage={item.aggregate.percentage} />
        </Cell>
      );
    },
  },
  {
    id: 'ownerEntityRef',
    label: 'Owner',
    defaultWidth: '15fr',
    isSortable: true,
    cell: item => (
      <Cell>
        <EntityRefLink entityRef={item.ownerEntityRef} underline="none" />
      </Cell>
    ),
  },
  {
    id: 'actions',
    label: '',
    defaultWidth: '5fr',
    cell: item => (
      <Cell>
        <Flex justify="end">
          <CheckActionsButton check={item} onDeleted={onDeleted} />
        </Flex>
      </Cell>
    ),
  },
];

type ChecksTableProps = {
  data: CheckRow[] | undefined;
  error: Error | undefined;
  columns: ColumnConfig<CheckRow>[];
};

const ChecksTable = ({ data, error, columns }: ChecksTableProps) => {
  const [selectedOwners, setSelectedOwners] = useUrlMultiSelect('owner');
  const [selectedKinds, setSelectedKinds] = useUrlMultiSelect('kind');
  const [searchQuery, setSearchQuery] = useUrlValue('search');
  const resetFilters = useResetUrlParams();
  const { labelByRef } = useMyTeams();

  const ownerOptions = useMemo(
    () => getOwnerOptions(data ?? [], labelByRef),
    [data, labelByRef],
  );
  const kindOptions = useMemo(() => getAffectedKindOptions(data ?? []), [data]);

  const filteredData = useMemo(() => {
    const owners = new Set(selectedOwners);
    const query = searchQuery.trim().toLowerCase();
    return (data ?? []).filter(item => {
      const matchesOwner = owners.size === 0 || owners.has(item.ownerEntityRef);
      const matchesSearch =
        query.length === 0 ||
        item.name.toLowerCase().includes(query) ||
        (item.description?.toLowerCase().includes(query) ?? false) ||
        item.ownerEntityRef.toLowerCase().includes(query) ||
        getOwnerLabel(item.ownerEntityRef, labelByRef)
          .toLowerCase()
          .includes(query);
      return (
        matchesOwner &&
        matchesAffectedKinds(item, selectedKinds) &&
        matchesSearch
      );
    });
  }, [data, selectedOwners, selectedKinds, searchQuery, labelByRef]);

  const sortChecks = useMemo(() => createSortChecks(labelByRef), [labelByRef]);

  const { tableProps } = useTable<CheckRow>({
    mode: 'complete',
    data: filteredData,
    initialSort: { column: 'name', direction: 'ascending' },
    sortFn: sortChecks,
    paginationOptions,
  });

  return (
    <Flex direction="column" gap="6">
      <DefinitionListFilterBar
        ownerLabel="Check owner"
        ownerOptions={ownerOptions}
        selectedOwners={selectedOwners}
        onOwnersChange={setSelectedOwners}
        kindOptions={kindOptions}
        selectedKinds={selectedKinds}
        onKindsChange={setSelectedKinds}
        search={{ value: searchQuery, onChange: setSearchQuery }}
        onClear={() => resetFilters(['owner', 'kind', 'search'])}
      />
      <Table
        columnConfig={columns}
        {...tableProps}
        emptyState="No checks found"
        error={error}
      />
    </Flex>
  );
};

export const PulseChecksPage = () => {
  const identityApi = useApi(identityApiRef);
  const [importResultStorageKey, setImportResultStorageKey] =
    useState<string>();
  const [importResult, setImportResult] = useState<CheckImportResult>();
  const { value: checks, loading, error, retry } = usePulseChecks();
  const checkIds = useMemo(
    () => checks?.map(check => check.id) ?? [],
    [checks],
  );
  const {
    value: aggregates,
    loading: aggregatesLoading,
    error: aggregatesError,
  } = useTrackChecks(checkIds);
  const getDetailsPath = useRouteRef(checksDetailsSubRouteRef);
  const getNewPath = useRouteRef(checksNewSubRouteRef);
  const { canCreate } = useCreateCheck();

  useEffect(() => {
    let active = true;
    setImportResult(undefined);
    setImportResultStorageKey(undefined);

    const loadImportResult = async (): Promise<void> => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        if (!active) {
          return;
        }

        const storageKey = getImportResultStorageKey(
          CHECK_IMPORT_RESULT_STORAGE_KEY,
          identity.userEntityRef,
        );
        globalThis.sessionStorage?.removeItem(CHECK_IMPORT_RESULT_STORAGE_KEY);
        setImportResultStorageKey(storageKey);
        setImportResult(readImportResult(storageKey, checkImportResultSchema));
      } catch {
        // Without an authenticated identity, no persisted result is safe to show.
      }
    };

    void loadImportResult();
    return () => {
      active = false;
    };
  }, [identityApi]);

  const columns = createColumns(getDetailsPath, retry, aggregatesLoading);
  const rows = useMemo(
    () =>
      checks?.map(check => ({
        ...check,
        aggregate: aggregates?.get(check.id),
      })),
    [checks, aggregates],
  );

  const createButton = canCreate ? (
    <ButtonLink
      variant="primary"
      iconStart={<RiAddLine />}
      href={getNewPath?.() ?? ''}
    >
      Create check
    </ButtonLink>
  ) : null;

  const importDialog = canCreate ? (
    <CheckImportDialog
      onImportStarted={() => {
        setImportResult(undefined);
        storeImportResult(importResultStorageKey);
      }}
      onImported={result => {
        setImportResult(result);
        storeImportResult(importResultStorageKey, result);
        if (result.imported.length > 0) {
          retry();
        }
      }}
    />
  ) : null;

  const exportButton = (
    <Button
      variant="secondary"
      iconStart={<RiDownloadLine />}
      isDisabled={!checks?.length}
      onClick={() => downloadChecksYaml(checks ?? [])}
    >
      Export checks
    </Button>
  );

  const headerActions = (
    <Flex gap="2">
      {exportButton}
      {importDialog}
      {createButton}
    </Flex>
  );

  return (
    <>
      {importResult ? (
        <Box px="4">
          <CheckImportResultBanner
            result={importResult}
            onDismiss={() => {
              setImportResult(undefined);
              storeImportResult(importResultStorageKey);
            }}
          />
        </Box>
      ) : null}
      <Header
        title="Checks"
        description="Checks are individual rules that evaluate catalog entities against collected facts, returning a pass or fail result for every applicable entity."
        customActions={headerActions}
      />
      <Container>
        {loading ? (
          <PulseListSkeleton />
        ) : (
          <ChecksTable data={rows} error={error} columns={columns} />
        )}
        {!loading && aggregatesError && (
          <Text color="danger" role="alert">
            Failed to load check results: {aggregatesError.message}
          </Text>
        )}
      </Container>
    </>
  );
};
